import type { Book } from '../types';

/**
 * Matching of search results against books already owned.
 *
 * The backend (`/api/library/owned`) sends identifiers for everything in the
 * Grimmory library plus this user's completed downloads. Normalization here must
 * stay in lockstep with `shelfmark/core/library_ownership.py`.
 */

export interface OwnedLibraryResponse {
  enabled?: boolean;
  library_configured?: boolean;
  library_count?: number;
  history_count?: number;
  provider_ids?: Record<string, string[]>;
  isbns?: string[];
  keys?: string[];
  count?: number;
  stale?: boolean;
  error?: string;
}

export interface OwnedBooksIndex {
  enabled: boolean;
  providerIds: Map<string, Set<string>>;
  isbns: Set<string>;
  keys: Set<string>;
}

export const EMPTY_OWNED_INDEX: OwnedBooksIndex = {
  enabled: false,
  providerIds: new Map(),
  isbns: new Set(),
  keys: new Set(),
};

const LEADING_ARTICLE = /^(?:the|a|an)\s+/;
// Anything that is not a Unicode letter or digit is a separator, so Slovenian and
// other non-ASCII titles normalize instead of collapsing to an empty string.
const SEPARATOR = /[^\p{L}\p{N}]+/gu;
const COMBINING_MARK = /\p{M}/gu;

const stripAccents = (value: string): string =>
  value.normalize('NFKD').replace(COMBINING_MARK, '');

export const normalizeTitle = (value: string | undefined | null): string => {
  if (typeof value !== 'string') return '';
  // Subtitles are inconsistent between providers; compare the main title only.
  const base = stripAccents(value).toLowerCase().split(':')[0];
  return base.replace(SEPARATOR, ' ').trim().replace(LEADING_ARTICLE, '').trim();
};

export const normalizeAuthor = (value: string | undefined | null): string => {
  if (typeof value !== 'string') return '';
  // "R.F. Kuang" and "R. F. Kuang" must collapse to the same key.
  return stripAccents(value).toLowerCase().replace(SEPARATOR, '');
};

export const normalizeIsbn = (value: string | undefined | null): string => {
  if (typeof value !== 'string') return '';
  const normalized = value.replace(/[^0-9xX]/g, '').toUpperCase();
  return normalized.length === 10 || normalized.length === 13 ? normalized : '';
};

export const titleAuthorKey = (
  title: string | undefined | null,
  author: string | undefined | null,
): string => {
  const normalizedTitle = normalizeTitle(title);
  const normalizedAuthor = normalizeAuthor(author);
  if (!normalizedTitle || !normalizedAuthor) return '';
  return `${normalizedTitle}|${normalizedAuthor}`;
};

export function buildOwnedIndex(response: OwnedLibraryResponse | null): OwnedBooksIndex {
  if (!response?.enabled) return EMPTY_OWNED_INDEX;

  const providerIds = new Map<string, Set<string>>();
  Object.entries(response.provider_ids ?? {}).forEach(([provider, ids]) => {
    if (!Array.isArray(ids)) return;
    providerIds.set(provider, new Set(ids.filter((id) => typeof id === 'string' && id.length > 0)));
  });

  return {
    enabled: true,
    providerIds,
    isbns: new Set((response.isbns ?? []).filter((isbn) => typeof isbn === 'string')),
    keys: new Set((response.keys ?? []).filter((key) => typeof key === 'string')),
  };
}

function bookAuthors(book: Book): string[] {
  const authors = Array.isArray(book.authors) ? book.authors : [];
  return [...authors, book.author, book.search_author].filter(
    (author): author is string => typeof author === 'string' && author.trim().length > 0,
  );
}

/** Whether a search result corresponds to a book that is already owned. */
export function isBookOwned(book: Book, index: OwnedBooksIndex): boolean {
  if (!index.enabled) return false;

  if (book.provider && book.provider_id) {
    if (index.providerIds.get(book.provider)?.has(book.provider_id)) return true;
  }

  for (const isbn of [book.isbn_13, book.isbn_10]) {
    const normalized = normalizeIsbn(isbn);
    if (normalized && index.isbns.has(normalized)) return true;
  }

  if (index.keys.size === 0) return false;

  for (const title of [book.title, book.search_title]) {
    for (const author of bookAuthors(book)) {
      const key = titleAuthorKey(title, author);
      if (key && index.keys.has(key)) return true;
    }
  }

  return false;
}
